import { Badge, Box, ErrorBoundary, Skeleton } from "@prismicio/editor-ui"
import { Suspense } from "react"

import { useRepositoryTypes } from "../../../app/services/customTypes"
import { useRepository } from "../../../app/services/repository"
import { type Filter, type FilterType } from "./useActionBar"
import { getAuthorDisplayName } from "./utils"

interface FiltersBarProps {
  selectedFilters: Filter[]
  onRemoveFilter: (filterId: string, filterType: FilterType) => void
}

export function FiltersBar(props: FiltersBarProps) {
  const { selectedFilters, onRemoveFilter } = props

  return (
    <Box gap={8} flexWrap="wrap">
      <Suspense fallback={<FilterBarLoading />}>
        <ErrorBoundary renderError={() => <FiltersBarError {...props} />}>
          {selectedFilters.map((filter) => (
            <Filter
              onRemoveFilter={onRemoveFilter}
              key={filter.value}
              filter={filter}
            />
          ))}
        </ErrorBoundary>
      </Suspense>
    </Box>
  )
}

interface FilterProps {
  onRemoveFilter: (filterId: string, filterType: FilterType) => void
  filter: Filter
}

function Filter(props: FilterProps) {
  const { onRemoveFilter, filter } = props

  const { value, type } = filter

  const label = useBadgeText(value, type)

  return (
    <Badge
      title={label}
      color="purple"
      size="medium"
      maxWidth={250}
      onClose={() => onRemoveFilter(value, type)}
    />
  )
}

function useBadgeText(value: string, type: FilterType) {
  const repositoryCustomTypes = useRepositoryTypes()
  const { authors: repositoryAuthors } = useRepository()

  switch (type) {
    case "customType": {
      const customTypeLabel =
        repositoryCustomTypes.find((customType) => customType.id === value)
          ?.label ?? value

      return `Type: ${customTypeLabel}`
    }

    case "tag":
      return `Tags: ${value}`

    case "author": {
      const author = repositoryAuthors.find((author) => author.id === value)
      const label = author ? getAuthorDisplayName(author) : value

      return `Author: ${label}`
    }
  }
}
function FiltersBarError(props: FiltersBarProps) {
  const { selectedFilters, onRemoveFilter } = props

  return (
    <>
      {selectedFilters.map((filter) => (
        <Filter
          key={filter.value}
          onRemoveFilter={onRemoveFilter}
          filter={filter}
        />
      ))}
    </>
  )
}

function FilterBarLoading() {
  return (
    <>
      <Skeleton color="grey5" borderRadius={6} width={80} height={24} />
      <Skeleton color="grey5" borderRadius={6} width={80} height={24} />
      <Skeleton color="grey5" borderRadius={6} width={80} height={24} />
      <Skeleton color="grey5" borderRadius={6} width={80} height={24} />
    </>
  )
}
